/**
 * F27 (Wave 2E) — WhatsApp delivery log panel.
 *
 * Mounts on `/admin/bookings/:id` (under F17 booking detail) alongside the
 * F20 PaymentMarksHistoryPanel + DisputeHistoryPanel. Surfaces F27 send
 * attempts for incident response — answers "what notifications fired for
 * booking Y" + "why did this user's WhatsApp message land here".
 *
 * Read endpoint: GET /admin/whatsapp/delivery-log?bookingId= or ?userId=
 * (gated on `whatsapp:read`). The panel renders nothing if the admin actor
 * lacks the perm (server returns 403 → component silently degrades).
 */
import { useEffect, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Badge, Card, EmptyState, Skeleton } from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import { listWhatsAppDeliveryLog, type DeliveryLogEntry } from '../../api/whatsapp';

export interface WhatsAppDeliveryLogPanelProps {
  /** Filter by booking id (preferred when mounted under booking detail). */
  readonly bookingId?: string;
  /** Filter by user id (alternative; one of bookingId/userId required). */
  readonly userId?: string;
}

function statusTone(
  status: DeliveryLogEntry['status'],
): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (status) {
    case 'delivered':
      return 'success';
    case 'pending_retry':
      return 'warning';
    case 'permanent_failure':
    case 'rate_limited':
      return 'danger';
    case 'suppressed_by_preference':
    case 'no_active_tokens':
    default:
      return 'neutral';
  }
}

function deliveryStateTone(
  state: DeliveryLogEntry['deliveryState'],
): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (state) {
    case 'read':
    case 'delivered':
      return 'success';
    case 'sent':
    case 'pending':
      return 'neutral';
    case 'failed':
      return 'danger';
    default:
      return 'neutral';
  }
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function WhatsAppDeliveryLogPanel(props: WhatsAppDeliveryLogPanelProps) {
  const { t, locale } = useI18n();
  const [rows, setRows] = useState<readonly DeliveryLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    if (!props.bookingId && !props.userId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const opts: { bookingId?: string; userId?: string; limit: number } = { limit: 50 };
    if (props.bookingId) opts.bookingId = props.bookingId;
    if (props.userId) opts.userId = props.userId;
    listWhatsAppDeliveryLog(opts)
      .then((result) => {
        if (cancelled) return;
        setRows(result.data);
      })
      .catch(() => {
        if (cancelled) return;
        setErrored(true);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [props.bookingId, props.userId]);

  // 403 / network error → render nothing so the booking detail page stays
  // clean for admins without whatsapp:read.
  if (errored) return null;

  return (
    <Card>
      <div className="flex flex-col gap-3" data-testid="whatsapp-delivery-log-panel">
        <div className="flex items-center gap-2">
          <UIIcon name="bell" size={18} color="var(--color-info)" />
          <h3 className="text-[15px] font-bold text-ink">
            {t('admin:whatsapp.delivery_log.panel_title', {
              defaultValue: 'WhatsApp delivery log',
            })}
          </h3>
        </div>
        <p className="text-[12px] text-ink-3">
          {t('admin:whatsapp.delivery_log.panel_description', {
            defaultValue:
              'Per-attempt records for incident response. Failed attempts surface the Meta error code so admins can diagnose why a message did not land.',
          })}
        </p>

        {loading ? (
          <div className="space-y-2" data-testid="whatsapp-delivery-log-loading">
            <Skeleton className="h-10 w-full rounded" />
            <Skeleton className="h-10 w-full rounded" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            title={t('admin:whatsapp.delivery_log.empty_state', {
              defaultValue: 'No WhatsApp attempts for this booking.',
            })}
          />
        ) : (
          <ul className="flex flex-col divide-y divide-hairline">
            {rows.map((row) => (
              <li
                key={row.id}
                className="flex flex-col gap-1.5 py-2.5"
                data-testid={`whatsapp-delivery-log-row-${row.id}`}
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 text-[12px]">
                    <span className="font-mono text-ink-2">{row.templateName ?? '—'}</span>
                    <span className="text-ink-3 text-[10.5px] uppercase font-mono tracking-wider">
                      ·
                    </span>
                    <span className="text-ink-2">{row.sourceEventKind}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      tone={statusTone(row.status)}
                      size="sm"
                      data-testid={`whatsapp-delivery-log-status-${row.id}`}
                    >
                      {t(`admin:whatsapp.delivery_log.status_values.${row.status}`, {
                        defaultValue: row.status,
                      })}
                    </Badge>
                    <Badge
                      tone={deliveryStateTone(row.deliveryState)}
                      size="sm"
                      data-testid={`whatsapp-delivery-log-delivery-state-${row.id}`}
                    >
                      {t(`admin:whatsapp.delivery_log.delivery_state.${row.deliveryState}`, {
                        defaultValue: row.deliveryState,
                      })}
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-[11px] text-ink-3 flex-wrap">
                  <span className="font-mono">
                    {t('admin:whatsapp.delivery_log.column_when', { defaultValue: 'When' })}:{' '}
                    {formatDate(row.createdAt, locale)}
                  </span>
                  <span>
                    {t('admin:whatsapp.delivery_log.column_attempts', {
                      defaultValue: 'Attempts',
                    })}
                    : {row.attemptCount}
                  </span>
                  {row.whatsappMessageId ? (
                    <span className="font-mono">{row.whatsappMessageId}</span>
                  ) : null}
                  {row.errorCode ? (
                    <span className="text-danger font-mono">
                      {t('admin:whatsapp.delivery_log.error_prefix', { defaultValue: 'err' })}{' '}
                      {row.errorCode}
                      {row.errorMessage ? `: ${row.errorMessage}` : ''}
                    </span>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}

export default WhatsAppDeliveryLogPanel;
